import * as React from 'react';
import { Result, Sample, SampleFactory, Parameter, ParameterFactory } from '../../resource/';
import { IAuthProps, Query } from '../../services/';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Section } from '../../modules';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { ValueDescription } from './ValueDescription';
import { ExtraValueDescription } from './ExtraValueDescription';
import { Label } from '@independent-software/typeui/controls/Label';

interface IProps {
  /** Initial form data. */
  data: Result;
  /** Called whenever form changes. */
  onChange: (data: Result, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

interface IState {
  /** Current form data. */
  data: Result;
  /** Data for samples dropdown */
  samples: Sample[];
  /** Data for parameters dropdown */
  parameters: Parameter[];
}

class ResultForm extends React.Component<IAuthProps & IProps, IState> {
  constructor(props: IAuthProps & IProps) {
    super(props);

    // Intialize state.
    this.state = {
      data: props.data,
      samples: [],
      parameters: []
    };    
  }

  componentDidMount = () => {
    this.handleSampleSearch();
    this.handleParameterSearch();
  }

  handleSampleSearch = (q?: string) => {
    // Retrieve a list of samples:
    let query = new Query('date', 'asc');
    if (q) query.setFilter('q', 'like', q);
    SampleFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ samples: res.items }));
  }

  handleParameterSearch = (q?: string) => {
    // Retrieve a list of parameters:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ParameterFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ parameters: res.items }));
  }
  
  renderSample = () => 
    <Form.Field
      required={{message: "Sample is required."}}
      width={1} label="Sample"
      name="sample" 
      value={this.state.data.sample}
      control={(<Dropdown onSearch={this.handleSampleSearch} fluid data={this.state.samples} placeholder="Sample" label={(item:Sample) => <><Label>{item.station.name}</Label> <Label>{item.campaign.name}</Label> <Label>{item.date}</Label></>}>
        <Dropdown.Column weight={1}>{(item:Sample) => item.station.name}</Dropdown.Column>
        <Dropdown.Column weight={2}>{(item:Sample) => item.campaign.name}</Dropdown.Column>
        <Dropdown.Column weight={1}>{(item:Sample) => item.date}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a sample."/>    

  renderParameter = () => 
    <Form.Field
      required={{message: "Parameter is required."}}
      width={1} label="Parameter"
      name="parameter" 
      value={this.state.data.parameter}
      control={(<Dropdown onSearch={this.handleParameterSearch} fluid data={this.state.parameters} placeholder="Parameter" label={(item:Parameter) => item.name}>
        <Dropdown.Column weight={1}>{(item:Parameter) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a parameter."/>    
  
  renderValue = () =>
    <Form.Field 
      name="value" label="Value"
      value={this.state.data.value}
      control={(<Input type="text" placeholder="Value" fluid/>)}
      required={{message: "A value is required."}}
      isFloat={{message: "Value must be numeric."}}
      hint={(<React.Fragment>Please enter a result value.</React.Fragment>)}/>

  renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={this.state.data.description}
      control={<Dropdown clearable placeholder="Description" fluid data={ValueDescription.getList()}
        label={(item) => ValueDescription.toName(item)}>
        <Dropdown.Column>{(item) => ValueDescription.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select a result description.</React.Fragment>)}/>
        
  renderExtraValue = () =>
    <Form.Field 
      name="extra_value" label="Extra value"
      value={this.state.data.extra_value}
      control={(<Input type="text" placeholder="Extra value" fluid/>)}
      isFloat={{message: "Extra value must be numeric."}}
      hint={(<React.Fragment>Please enter an extra value.</React.Fragment>)}/>

  renderExtraDescription = () =>
    <Form.Field 
      name="extra_description" label="Extra description"
      value={this.state.data.extra_description}
      control={<Dropdown clearable placeholder="Extra description" fluid data={ExtraValueDescription.getList()}
        label={(item) => ExtraValueDescription.toName(item)}>
        <Dropdown.Column>{(item) => ExtraValueDescription.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select an extra description.</React.Fragment>)}/>      

  renderMinvalue = () =>
    <Form.Field 
      name="minvalue" label="Minimum value"
      value={this.state.data.minvalue}
      control={(<Input type="text" placeholder="Minimum value" fluid/>)}
      isFloat={{message: "Minimum value must be numeric."}}
      hint={(<React.Fragment>Please enter a minimum value.</React.Fragment>)}/>      

  renderMaxvalue = () =>
    <Form.Field 
      name="maxvalue" label="Maximum value"
      value={this.state.data.maxvalue}
      control={(<Input type="text" placeholder="Maximum value" fluid/>)}
      isFloat={{message: "Maximum value must be numeric."}}
      hint={(<React.Fragment>Please enter a maximum value.</React.Fragment>)}/>            

  renderReplicates = () =>
    <Form.Field 
      name="replicates" label="Replicates"
      value={this.state.data.replicates}
      control={(<Input type="text" placeholder="Replicates" fluid/>)}
      isInt={{message: "Replicates must be a whole number."}}
      hint={(<React.Fragment>Please enter a number of replicates.</React.Fragment>)}/>                  

  renderDetectionLimit = () =>
    <Form.Field 
      name="detection_limit" 
      value={this.state.data.detection_limit == 1}
      control={(<Checkbox label="Detection limit" checked={this.state.data.detection_limit == 1}/>)}
      />  

  render() {
    let p = this.props;
    return (
      <Form
        data={this.state.data} dirty={p.dirty} onChange={p.onChange} onValidate={p.onValidate}>
        <Tabs underlined>
          <Tabs.Pane label="Data">
            <Section padded>
              <Flex.Columns count={2}>
                <>
                  {this.renderSample()}
                  {this.renderParameter()}
                  {this.renderValue()}
                  {this.renderDescription()}
                  {this.renderDetectionLimit()}
                </>
                <>
                </>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
          <Tabs.Pane label="Additional data">
            <Section padded>
              <Flex.Columns count={2}>
                <>{this.renderExtraValue()}</>
                <></>
                <>{this.renderExtraDescription()}</>
                <>{this.renderMinvalue()}</>
                <>{this.renderMaxvalue()}</>
                <>{this.renderReplicates()}</>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
        </Tabs>
      </Form>
    )
  }
}

export { ResultForm };
